"use client";

import { FC, useLayoutEffect } from "react";
import {
  GlobalNavigation,
  SuperHeaderItem,
} from "@/components/GlobalNavigation";
import { Link } from "@/types/General";
import { Blocks, BuilderBlock } from "@builder.io/sdk-react";
import { BuilderProps } from "@/types/builder/BuilderProps";
import { LocaleIsoCode } from "@/types";
import { ISO_TO_LOCALE } from "@/helpers";

type GlobalNavigationBlockProps = {
  superHeader: SuperHeaderItem;
  logo?: BuilderBlock[];
  mobileLogoExpanded?: BuilderBlock[];
  cta?: BuilderBlock[];
  colorVariant?: "coolGray" | "blue" | "gray";
  mobileHamburgerMenuCollapsedDLD?: string;
  mobileHamburgerMenuCollapsedDLT?: string;
  mobileHamburgerMenuExpandedDLD?: string;
  mobileHamburgerMenuExpandedDLT?: string;
  mobileBackLink?: {
    dlt?: string;
    dld?: string;
    backToText?: string;
    mainMenuText?: string;
  };
  menuItems?: ({
    leftContent?: BuilderBlock[];
    leftCta?: BuilderBlock[];
    leftLinksTitle?: string;
    leftLinks?: Link[];
    categoryLinksInTwoColumns?: boolean;
    categoryLinks?: {
      categoryTitle?: string;
      categoryTitleMobileDLD?: string;
      categoryTitleMobileDLT?: string;
      categoryDescription?: BuilderBlock[];
      linksColumnCount?: "1" | "2" | "3";
      icon?: BuilderBlock[];
      cta?: BuilderBlock[];
      links?: Link[];
    }[];

    bottomLinksTitle?: string;
    bottomLinks?: Link[];
  } & Link)[];
  contactLink?: Link;
  loginMenu?: {
    ariaLabel?: string;
    dlt?: string;
    dld?: string;
    links?: Link[];
  };
  isConsolidatedNavigation?: boolean;
  isDocked?: boolean;
} & BuilderProps;

export const GlobalNavigationBlock: FC<GlobalNavigationBlockProps> = ({
  isConsolidatedNavigation = false,
  isDocked = false,
  superHeader,
  logo,
  mobileLogoExpanded,
  cta,
  colorVariant,
  menuItems,
  contactLink,
  loginMenu,
  builderBlock,
  builderComponents,
  builderContext,
  builderLinkComponent,
  ...restProps
}) => {
  useLayoutEffect(() => {
    if (!isDocked) {
      return;
    }

    const updateWrapperPosition = () => {
      const isPageWithSecondary = !!document.getElementById("secondaryNav");
      const isDockedWithSecondaryNav = isDocked && isPageWithSecondary;

      const wrapperElement: HTMLElement | null = document.querySelector(
        '[builder-model="page-header"]',
      );

      if (!wrapperElement) {
        return;
      }

      wrapperElement.style.position = isDockedWithSecondaryNav
        ? "relative"
        : "";
    };

    updateWrapperPosition();
  }, [isDocked]);

  return (
    <>
      <GlobalNavigation
        locale={
          ISO_TO_LOCALE[builderContext.rootState.locale as LocaleIsoCode] ||
          "en-us"
        }
        sticky={builderContext.content?.data?.isSticky}
        isConsolidatedNavigation={isConsolidatedNavigation}
        isDocked={isDocked}
        asPath={builderContext.context.asPath as string}
        superHeader={superHeader}
        contactLink={contactLink}
        loginMenu={loginMenu}
        menu={{
          ...restProps,
          colorVariant: colorVariant || "coolGray",
          items: menuItems?.map((item, index) => ({
            ...item,
            leftContent: (
              <Blocks
                key={`left-content-${index}`}
                parent={builderBlock?.id}
                blocks={item.leftContent || []}
                path={`menuItems.${index}.leftContent`}
                registeredComponents={builderComponents}
                linkComponent={builderLinkComponent}
                context={builderContext}
              />
            ),
            leftCta: (
              <Blocks
                key={`left-cta-${index}`}
                parent={builderBlock?.id}
                blocks={item.leftCta || []}
                path={`menuItems.${index}.leftCta`}
                registeredComponents={builderComponents}
                linkComponent={builderLinkComponent}
                context={builderContext}
              />
            ),
            categoryLinks: item?.categoryLinks?.map((category, catIndex) => ({
              ...category,
              categoryDescription: (
                <Blocks
                  key={`cat-desc-${catIndex}`}
                  parent={builderBlock?.id}
                  blocks={category.categoryDescription || []}
                  path={`menuItems.${index}.categoryLinks.${catIndex}.categoryDescription`}
                  registeredComponents={builderComponents}
                  linkComponent={builderLinkComponent}
                  context={builderContext}
                />
              ),
              icon: (
                <Blocks
                  key={`cat-icon-${catIndex}`}
                  parent={builderBlock?.id}
                  blocks={category.icon || []}
                  path={`menuItems.${index}.categoryLinks.${catIndex}.icon`}
                  registeredComponents={builderComponents}
                  linkComponent={builderLinkComponent}
                  context={builderContext}
                />
              ),
              cta: (
                <Blocks
                  key={`cat-cta-${catIndex}`}
                  parent={builderBlock?.id}
                  blocks={category.cta || []}
                  path={`menuItems.${index}.categoryLinks.${catIndex}.cta`}
                  registeredComponents={builderComponents}
                  linkComponent={builderLinkComponent}
                  context={builderContext}
                />
              ),
            })),
          })),
          logo: (
            <Blocks
              parent={builderBlock?.id}
              blocks={logo || []}
              path="logo"
              registeredComponents={builderComponents}
              linkComponent={builderLinkComponent}
              context={builderContext}
            />
          ),
          mobileLogoExpanded: (
            <Blocks
              parent={builderBlock?.id}
              blocks={mobileLogoExpanded || []}
              path="mobileLogoExpanded"
              registeredComponents={builderComponents}
              linkComponent={builderLinkComponent}
              context={builderContext}
            />
          ),
          cta: (
            <Blocks
              parent={builderBlock?.id}
              blocks={cta || []}
              path="cta"
              registeredComponents={builderComponents}
              linkComponent={builderLinkComponent}
              context={builderContext}
            />
          ),
        }}
      />
    </>
  );
};
